import React, { createContext, useContext, useEffect, useState } from "react";
import type { AuthContextType, AuthStateProps } from "./types";
import { defaultValues } from "./defaultValues";
import { useNavigate } from "react-router-dom";
import { authService } from "../../services/modules/auth";

const initialConfig = { ...defaultValues };

const AuthContext = createContext<AuthContextType>(initialConfig);

export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();

  const [authState, setAuthState] = useState<AuthStateProps>(
    initialConfig.authState
  );

  const updateState = (newState: Partial<AuthStateProps>) => {
    setAuthState((prevState: AuthStateProps) => ({
      ...prevState,
      ...newState,
    }));
  };

  const decodeToken = (token: string) => {
    const base64Payload = token.split(".")[1];
    const decodedPayload = JSON.parse(atob(base64Payload));

    return decodedPayload.permission;
  };

  const logout = () => {
    localStorage.clear();
    navigate("/");
  };

  const persistToken = (token: string) => {
    updateState({ accessToken: token });

    localStorage.setItem("coprodux-token", token);

    try {
      const permission = decodeToken(token);

      let route = "/empresas";

      if (permission === "master") {
        route = "/admin";
      } else if (permission === "trial") {
        route = "/trial";
      }
      navigate(route);
    } catch (error) {
      navigate("/");
    }
  };

  const getAccountData = () => {
    authService
      .getAccount()
      .then((response) => updateState({ accountData: response }));
  };

  useEffect(() => {
    if (!(authState.accessToken || localStorage.getItem("coprodux-token"))) {
      navigate("/");
    } else {
      const token =
        authState.accessToken || localStorage.getItem("coprodux-token");
      if (token) {
        const decoded = decodeToken(token);
        if (decoded !== "trial") getAccountData();
      }
    }
  }, [authState.accessToken]);

  const value = {
    authState,
    persistToken,
    logout,
    updateState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => useContext(AuthContext);

export default useAuth;
